"use client";

import { useState } from "react";
import {
  Notice,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  alphaOptions,
  ExampleButton,
} from "@/components/tools/stats-tests/kit";
import { TwoWayResults } from "@/components/tools/stats-tests/two-way-results";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  MAX_LEVELS,
  parseLongTable,
  type TwoWayOutcome,
  twoWayAnova,
} from "@/lib/tool-kit/stats-tests/two-way-anova";

/** English strings. A translated content file overrides these through `ui`. */
export const TWO_WAY_ANOVA_CALCULATOR_STRINGS = {
  factorAName: "Factor A name",
  factorBName: "Factor B name",
  factorADefault: "Factor A",
  factorBDefault: "Factor B",
  dataLabel: "Data — one observation per line: level of A, level of B, value",
  dataHint:
    "Separate the three fields with commas, tabs, or semicolons (use tabs or semicolons if your numbers have a decimal comma). A header line is skipped, so you can paste straight from a spreadsheet.",
  dataPlaceholder: "Low, Morning, 12\nLow, Evening, 11\nHigh, Morning, 18",
  observationsRead: "one: observation read|other: observations read",
  linesSkipped: "one: {count} line skipped|other: {count} lines skipped",
  alphaLabel: "Significance level (α)",
  tryExample: "Try an example",
  exampleFactorA: "Caffeine",
  exampleFactorB: "Time of day",
  exampleData:
    "Caffeine, Time, Score\nLow, Morning, 11\nLow, Morning, 13\nLow, Morning, 15\nLow, Afternoon, 14\nLow, Afternoon, 16\nLow, Afternoon, 18\nLow, Evening, 9\nLow, Evening, 11\nLow, Evening, 13\nHigh, Morning, 17\nHigh, Morning, 19\nHigh, Morning, 21\nHigh, Afternoon, 20\nHigh, Afternoon, 22\nHigh, Afternoon, 24\nHigh, Evening, 9\nHigh, Evening, 11\nHigh, Evening, 13",
  results: "Results",
  balanced:
    "Balanced design: every cell has {n} observations, so Type I, II, and III sums of squares agree.",
  unbalanced:
    "Unbalanced design: cell sizes differ, so the table uses Type III sums of squares — each effect adjusted for the others, as SPSS reports by default. Type III sums of squares need not add up to the total.",
  tableHeading: "Two-way ANOVA table (Type III sums of squares)",
  source: "Source",
  ss: "SS",
  df: "df",
  ms: "MS",
  f: "F",
  p: "p",
  partialEta: "ηₚ²",
  interaction: "{a} × {b}",
  error: "Error (within cells)",
  total: "Total",
  decisionReject: "{effect}: significant at α = {alpha} (p = {p}).",
  decisionKeep: "{effect}: not significant at α = {alpha} (p = {p}).",
  interactionNote:
    "The interaction is significant: the effect of one factor depends on the level of the other, so read the main effects through the cell means and the plot rather than on their own.",
  cellHeading: "Cell means",
  cellHint:
    "Each cell shows mean (SD, n). The last row and column are unweighted marginal means — the means the Type III tests compare.",
  marginal: "Marginal mean",
  cellValue: "{mean} ({sd}, n = {n})",
  plotHeading: "Interaction plot",
  plotHint:
    "Parallel lines suggest no interaction; lines that converge, diverge, or cross suggest one.",
  plotLabel: "Interaction plot of cell means: lines for {trace} across {x}",
  yTitle: "Cell mean",
  swapAxes: "Put {a} on the horizontal axis",
  downloadPlot: "Download plot (SVG)",
  howCalculated: "How this was calculated",
  workingError:
    "Error: SSₑ = Σ (y − its cell mean)² = {ss}; dfₑ = N − ab = {n} − {cells} = {df}; MSₑ = SSₑ / dfₑ = {ms}",
  workingMethod:
    "Each effect's Type III SS = (Lμ̂)′(LD⁻¹L′)⁻¹(Lμ̂), where μ̂ holds the cell means, D the cell sizes, and L the contrasts that define the effect.",
  workingEffect:
    "{effect}: F = (SS / df) / MSₑ = ({ss} / {df}) / {ms} = {f}; ηₚ² = SS / (SS + SSₑ) = {eta}",
  apaHeading: "APA-style result",
  apaTemplate: "{effect}, *F*({df1}, {df2}) = {f}, *p* {p}, ηₚ² = {eta}",
  copyApa: "Copy APA result",
  copied: "Copied!",
  prompt:
    "Enter observations for every combination of the two factors' levels, with at least one combination measured more than once.",
  errorLevels: "Each factor needs between 2 and {max} levels.",
  errorEmpty:
    "There are no observations for {cell}. Every combination of levels needs at least one value.",
  errorReplication:
    "Every cell has a single value, so there is no within-cell variation to test against. Add replicates.",
  errorVariance:
    "Every value equals its cell mean, so the error variance is zero and F is undefined.",
  errorSingular:
    "The design could not be solved. Check the level names for typos that create extra levels.",
} as const;

type Strings = typeof TWO_WAY_ANOVA_CALCULATOR_STRINGS;

function failureMessage(
  outcome: Extract<TwoWayOutcome, { ok: false }>,
  strings: Strings
): string {
  const messages: Record<string, string> = {
    "too-few-levels": t(strings.errorLevels, { max: MAX_LEVELS }),
    "too-many-levels": t(strings.errorLevels, { max: MAX_LEVELS }),
    "empty-cell": t(strings.errorEmpty, { cell: outcome.detail ?? "" }),
    "no-replication": strings.errorReplication,
    "no-variance": strings.errorVariance,
    singular: strings.errorSingular,
  };
  return messages[outcome.reason] ?? strings.prompt;
}

export default function TwoWayAnovaCalculatorWidget() {
  const strings = useToolUi(TWO_WAY_ANOVA_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [factorA, setFactorA] = useState<string>(strings.factorADefault);
  const [factorB, setFactorB] = useState<string>(strings.factorBDefault);
  const [data, setData] = useState("");
  const [alpha, setAlpha] = useState("0.05");

  const parsed = parseLongTable(data, parseNumberInput);
  const count = parsed.observations.length;
  const outcome = count > 0 ? twoWayAnova(parsed.observations) : null;
  const hint = [
    `${fmt(count)}${tPlural(strings.observationsRead, locale, count)}`,
    parsed.skipped > 0
      ? tPlural(strings.linesSkipped, locale, parsed.skipped, {
          count: fmt(parsed.skipped),
        })
      : "",
  ]
    .filter((part) => part !== "")
    .join(" · ");

  function fillExample() {
    setFactorA(strings.exampleFactorA);
    setFactorB(strings.exampleFactorB);
    setData(strings.exampleData);
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          id="twa-factor-a"
          label={strings.factorAName}
          onChange={setFactorA}
          value={factorA}
        />
        <TextField
          id="twa-factor-b"
          label={strings.factorBName}
          onChange={setFactorB}
          value={factorB}
        />
      </div>
      <TextAreaField
        hint={
          <>
            {strings.dataHint}
            <br />
            {hint}
          </>
        }
        id="twa-data"
        label={strings.dataLabel}
        onChange={setData}
        placeholder={strings.dataPlaceholder}
        rows={10}
        value={data}
      />
      <div className="flex flex-wrap items-end gap-4">
        <div className="w-full max-w-xs">
          <SelectField
            id="twa-alpha"
            label={strings.alphaLabel}
            onChange={setAlpha}
            options={alphaOptions(fmt)}
            value={alpha}
          />
        </div>
        <ExampleButton label={strings.tryExample} onClick={fillExample} />
      </div>
      {outcome?.ok ? (
        <TwoWayResults
          alpha={Number(alpha)}
          factorA={factorA.trim() || strings.factorADefault}
          factorB={factorB.trim() || strings.factorBDefault}
          fmt={fmt}
          r={outcome.result}
          strings={strings}
        />
      ) : (
        <Notice>
          {outcome && !outcome.ok
            ? failureMessage(outcome, strings)
            : strings.prompt}
        </Notice>
      )}
    </div>
  );
}
